import { PortableText } from '@portabletext/react';
import { Body, H2, H3, PrimaryButton, Tag, TextField } from '@rikstv/shared-components';
import { use, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Icon, Loader } from '~/components/shared';
import { FlexBox } from '~/components/shared/layout/FlexBox';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import classes from './InternetLandingPage.module.css';
import { Route_InternetOfferSubPage } from './InternetOfferSubPage';
import { displayDataPromise, ispAddressLookup, ispAddressRefine, type SearchResponse } from './isp.api';
import { type InternetCustomerAddress, useInternetFlowStore } from './useInternetStore';
import { captureUXEvent } from '~/analytics/productAnalytics';
import { trackClickCheckAddress, trackInternetSalesView } from '~/analytics/eventTracker';

export const Route_InternetLandingPage = 'internett';

const INPUT_ID = 's-input';

export const InternetLandingPage = () => {
  const { data, isLoading, setSearchString, selectedAddress, setSelectedAddress } = useAdressSearch();
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { title, description, image } = use(displayDataPromise);

  useEffect(() => {
    trackInternetSalesView();
  }, []);

  useEffect(() => {
    if (searchResultsRef.current) {
      if (data) {
        searchResultsRef.current.style.display = 'block';
      } else {
        searchResultsRef.current.style.display = 'none';
        // try-focus on the input
        (document.getElementById(`${INPUT_ID}`) as HTMLInputElement | null)?.focus();
      }
    }
  }, [data]);
  const popoverSpacing = useMemo(() => {
    const length = data?.installationAddresses.length ?? 0;
    if (length === 0) return 0;
    return Math.min(10, length) * (16 * 2.5);
  }, [data?.installationAddresses.length]);
  // TODO: Move to custom hook?
  const onSelectAutocompleteSuggestion = useCallback(
    (addr: InternetCustomerAddress) => {
      if (addr.count === 1) {
        setSelectedAddress(addr);
      } else {
        setSearchString(addr);
      }
      const input = document.getElementById('s-input') as HTMLInputElement | null;
      if (input) {
        input.value = addr.description;
      }
      captureUXEvent('rtv_address_suggestion_selected', { addressDescription: addr.description, addressCount: addr.count });
    },
    [setSelectedAddress, setSearchString],
  );
  // TODO: Move to custom hook?
  const onClickCheckAvailability = useCallback(() => {
    if (selectedAddress) {
      trackClickCheckAddress({ city: selectedAddress.city, postal_code: selectedAddress.postalCode });
      navigate(Route_InternetOfferSubPage);
    }
  }, [selectedAddress, navigate]);
  const handleArrowKeyNavigationInPopupList: React.KeyboardEventHandler = useCallback((e) => {
    // only handle vertical arrow keys
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    // opt out when meta keys are pressed
    if (!(e.target instanceof HTMLElement) || e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return;
    // opt out when active element is not inside of the popover
    if (!e.target.contains(document.activeElement)) return;

    const liElement = document.activeElement?.parentElement;
    const nextElement = (e.key === 'ArrowDown' ? liElement?.nextElementSibling : liElement?.previousElementSibling)?.querySelector(
      'button',
    );
    if (nextElement) nextElement.focus();
  }, []);

  return (
    <>
      <main
        className={`main-layout container padded wide ${classes.main}`}
        style={{ paddingBottom: `${popoverSpacing > 0 ? popoverSpacing + 24 : 0}px` }}
      >
        <div className={classes.imageContainer}>
          <img src={`${image}?auto=format&w=1200`} alt="" />
        </div>
        <Card noMargin>
          <FlexBox direction="column" gap="calc(1.5rem + var(--rds-fluid-spacing--0-24))">
            <Tag className={classes.tag}>— TV og underholdning via internett</Tag>
            <FlexBox direction="column">
              <H2>{title}</H2>
              <Body as="div" className={classes.portableText}>
                <PortableText value={description} />
              </Body>
            </FlexBox>
            <Card color="gray-dark">
              <section>
                <H3>Sjekk tilgjengelighet der du bor</H3>
                <br />
                <div className={classes.searchWrapper}>
                  <TextField
                    id={INPUT_ID}
                    data-testid="address-search-input"
                    aria-controls="search-results"
                    defaultValue={selectedAddress?.description ?? ''}
                    className={classes.input}
                    name="adr-search"
                    label="Søk på din addresse"
                    autoComplete="street-address"
                    onChange={(e) => {
                      setSearchString(e.target.value);
                    }}
                  />
                  <span style={{ position: 'absolute', top: '55%', right: '1rem', transform: 'translateY(-50%)' }}>
                    <MapIcon />
                  </span>
                  <div id="search-results" ref={searchResultsRef} className={classes.searchResults}>
                    {isLoading && (
                      <div style={{ paddingBlock: '1rem' }}>
                        <Loader />
                      </div>
                    )}
                    {!isLoading && (data?.installationAddresses.length ?? 0) > 0 && (
                      <ul onKeyDown={handleArrowKeyNavigationInPopupList} data-testid="address-search-results">
                        {data?.installationAddresses.map((addr) => (
                          <li key={addr.description}>
                            <button onClick={() => onSelectAutocompleteSuggestion(addr)}>{addr.description}</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <br />
                <PrimaryButton
                  data-testid="check-availability-button"
                  onClick={onClickCheckAvailability}
                  className={classes.availabilityButton}
                  disabled={!selectedAddress}
                >
                  Sjekk tilgjengelighet
                  <Icon
                    type="search"
                    color="white"
                    size={1.5}
                    style={{ fill: selectedAddress ? 'white' : 'var(--common-button--disabled-color)' }}
                  />
                </PrimaryButton>
              </section>
            </Card>
          </FlexBox>
        </Card>
      </main>
      <SupportFooter />
    </>
  );
};

const useAdressSearch = () => {
  const { selectedAddress, setSelectedAddress } = useInternetFlowStore();
  const [searchString, setSearchString] = useState('');
  const [debouncedText, setDebouncedText] = useState('');
  const [data, setData] = useState<SearchResponse | null>(null);

  const [isPending, transition] = useTransition();

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedText(searchString);
    }, 450);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchString]);

  useEffect(() => {
    if (debouncedText.length > 4) {
      transition(async () => {
        setData(await ispAddressLookup(debouncedText));
      });
    } else {
      setData(null);
    }
  }, [debouncedText]);

  useEffect(() => {
    if (selectedAddress) {
      setData(null);
    }
  }, [selectedAddress]);

  /**
   * Pass false as a second argument to perform search immediately without debounce.
   */
  const exportedSetSearchString = useCallback(
    (value: string | InternetCustomerAddress) => {
      if (typeof value === 'string') {
        setSearchString(value);
        // if the difference in length is, say, more than 6 charachters assume autocomplete and update debouncedText immediately
        if (value.length - debouncedText.length > 6) {
          setDebouncedText(value);
        }
      } else {
        transition(async () => {
          setData(await ispAddressRefine(value));
        });
      }
    },
    [debouncedText],
  );

  // TODO: error handling, especially 0 length results
  return {
    data,
    isLoading: isPending,
    setSearchString: exportedSetSearchString,
    selectedAddress,
    setSelectedAddress,
  };
};

const MapIcon = () => (
  <svg width="13" height="19" viewBox="0 0 13 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>Map Marker Icon</title>
    <path
      d="M11.4375 6.40234C11.4375 3.6064 9.17094 1.33984 6.375 1.33984C3.57906 1.33984 1.3125 3.6064 1.3125 6.40234C1.3125 6.93209 1.51698 7.73038 1.89111 8.7124C2.25875 9.67736 2.76657 10.7612 3.32153 11.8391C4.29269 13.7253 5.39284 15.5634 6.09814 16.665L6.375 17.0898L6.65186 16.665C7.35716 15.5634 8.45731 13.7253 9.42847 11.8391C9.98343 10.7612 10.4913 9.67736 10.8589 8.7124C11.233 7.73038 11.4375 6.93209 11.4375 6.40234ZM8.625 6.40234C8.625 5.1597 7.61764 4.15234 6.375 4.15234C5.13236 4.15234 4.125 5.1597 4.125 6.40234C4.125 7.64498 5.13236 8.65234 6.375 8.65234C7.61764 8.65234 8.625 7.64498 8.625 6.40234ZM9.75 6.40234C9.75 8.2663 8.23896 9.77734 6.375 9.77734C4.51104 9.77734 3 8.2663 3 6.40234C3 4.53838 4.51104 3.02734 6.375 3.02734C8.23896 3.02734 9.75 4.53838 9.75 6.40234ZM12.5625 6.40234C12.5625 7.15022 12.2915 8.1114 11.9099 9.11304C11.5218 10.1317 10.9934 11.2562 10.4282 12.354C9.2978 14.5495 7.9983 16.6786 7.3125 17.7124L7.31177 17.7117C7.10333 18.0258 6.75203 18.2148 6.375 18.2148C5.99793 18.2148 5.64594 18.0259 5.4375 17.7117V17.7124C4.7517 16.6786 3.4522 14.5495 2.32178 12.354C1.75656 11.2562 1.22819 10.1317 0.840088 9.11304C0.458481 8.1114 0.1875 7.15022 0.1875 6.40234C0.1875 2.98508 2.95774 0.214844 6.375 0.214844C9.79226 0.214844 12.5625 2.98508 12.5625 6.40234Z"
      fill="#313131"
    />
  </svg>
);
